/**
 * The frame every page sits in — the nav, the search box, the account chip, the offline notice — plus
 * the two words `ui/State.tsx` needs, which belong to no screen because every screen uses them.
 *
 * The nav labels are keyed by core's `NavDestinationId` (R7's other side: core ships the destination
 * table, the platform ships the words), and the sign-in page reads the same table so that "the place
 * you were heading" is spelled the same way in both.
 */
export const shell = {
  brand: 'Visious',
  brandTitle: 'Visious home',
  skipToContent: 'Skip to content',

  nav: {
    label: 'Sections',
    popular: 'Popular',
    trending: 'Trending',
    subscriptions: 'Subscriptions',
    playlists: 'Playlists',
    history: 'History',
    lockedTitle: (destination: string): string =>
      `Sign in to your instance to use ${destination}`,
    /**
     * The gate said out loud, as the second half of a locked item's accessible name — the padlock beside
     * it is `aria-hidden` decoration. It was an inline literal in `AppShell` (and one carrying an em
     * dash, which the house style bans), so it could not be translated and was not reviewable as copy.
     */
    signInNeeded: 'Sign-in needed',
  },

  search: {
    label: 'Search',
    /** Also the paste-a-link affordance — an address opens directly, words search. */
    placeholder: 'Search, or paste a link',
    clear: 'Clear the search',
    /** The magnifier at the bar's end — the same submit Enter performs. */
    submit: 'Search',
  },

  account: {
    /** Neutral until the session is *known* — "Sign in" flashed at a signed-in reader reads as a logout. */
    unknown: 'Account',
    signIn: 'Sign in',
    signedInTitle: 'Your session on this instance',
    signedOutTitle: 'Sign in to your instance to use Subscriptions, Playlists and History',
  },

  settings: 'Settings',

  theme: {
    toLight: 'Switch to the light theme',
    toDark: 'Switch to the dark theme',
  },

  /** 4d. `live`, because it appears in response to the connection going rather than on arrival. */
  offline:
    'You’re offline. Visious itself still opens, but everything in it comes from your instance, so nothing below can load until the connection is back.',

  /** The renewal prompt (2026-08-02) — only for a session that cannot renew itself. */
  sessionExpiry: {
    /** `when` arrives formatted — "in 2 days" — so the sentence never conjugates a date. */
    text: (when: string): string =>
      `Your session expires ${when}. Signing in again keeps your subscriptions, playlists and history reachable.`,
    renew: 'Renew now',
    dismiss: 'Not now',
  },

  /** The two-click deletion: the word every armed remove or unsubscribe button wears. */
  confirm: 'Confirm',

  /** `ui/State.tsx`'s defaults — the retry every `ProblemPanel` offers, and the boundary's own words. */
  state: {
    retry: 'Try again',
  },

  /** `shell/ScreenBoundary.tsx` — a render that *throws* is a state too, and it lives inside the shell. */
  boundary: {
    title: 'This page could not be drawn',
    detailBefore:
      'Something in Visious went wrong while drawing it, which is a bug here, not anything your instance did. Everything else still works, and the navigation above is live. The error was:',
    retry: 'Try drawing it again',
  },

  /**
   * The three words the placeholder page and the resolve page both wear, neither of which had them:
   * both were JSX text nodes, so both rendered English under any catalogue.
   *
   * `openOnBefore` and `plannedInSliceBefore` end where an element begins — a styled note, a list of
   * plain anchors — so they are sentence *heads*, the shape `login.page.footnoteBefore` already uses.
   * The punctuation after the element stays in the view, because a full stop is not translatable.
   */
  placeholder: {
    plannedInSliceBefore: 'Planned for',
    openOnBefore: 'Open it on',
    backToStart: 'Back to the start',
  },

  /** The two `placeholder` screens the router can produce (`router/screens.ts`). */
  notFound: {
    title: 'Nothing here',
    detail: (path: string): string =>
      `${path} does not match anything in Visious. Either it names nothing, or the id in it is the wrong shape.`,
  },
  post: {
    title: 'Community posts are not supported',
    detail:
      'This is a real community post and the link is good. Visious just cannot show them yet.',
  },
} as const;
